import { useState } from 'react'
import { ArrowLeft, Check, Info } from '@digital-twins/design-system/icons'
import { Button } from '@design-system/components/button'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageContent } from '@/components/layout/PageContent'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { usersApi } from '@/lib/api'
import { useRouter } from '@/routing/navigation'
import { ROUTES } from '@/routes/routes'
import useSWR from 'swr'
import { useAuth } from '@/lib/config/auth-provider'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'
import {
  CONTACT_SALES_URL,
  UNLIMITED_SEATS_THRESHOLD,
  approxMaxSeats,
} from '@/pages/SettingsPage/components/billing/planConfig'
import {
  enabledCustomerFeatures,
  type CustomerFeatureFlag,
} from '@/pages/SettingsPage/components/billing/customerFeatures'
import { formatUsd } from '@/pages/SettingsPage/components/billing/formatMoney'
import { HowCreditsWorkDialog } from '@/pages/SettingsPage/components/billing/HowCreditsWorkDialog'
import { creditRateCentsFromInfo } from '@/pages/SettingsPage/components/billing/useCreditRateCents'
import { isMeteringEnrolled } from '@/lib/billing/metering'
import { isFairgenInternalTier, isTrialLike } from '@/lib/billing/tier'

interface PlanRow {
  id: string
  label: string
  /** Optional "?" affordance next to the row label. */
  tooltip?: string
  /** `true` renders the check; a string renders as text. */
  value: true | string
  isShown: boolean
}

function PlanRowValue({ value }: { value: PlanRow['value'] }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15">
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
        </div>
      </div>
    )
  }
  return (
    <Text size="sm" className="text-center">
      {value}
    </Text>
  )
}

export default function SettingsBillingPlansPage() {
  const { t, i18n } = useTranslation('app')
  const locale = intlLocaleForUi(i18n.language)
  const router = useRouter()
  const [isCostsOpen, setIsCostsOpen] = useState(false)

  const { isAuthenticated } = useAuth()
  const { data: profile } = useSWR(
    isAuthenticated ? 'profile' : null,
    () => usersApi.getProfile(),
    { revalidateOnFocus: false },
  )

  // Skeleton-gate before reading any profile fields so we don't transiently
  // assume a tier.
  if (!profile) {
    return (
      <PageLayout className="max-w-full pt-14 py-8 md:pt-8">
        <PageContent className="gap-8">
          <Skeleton className="h-96 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </PageContent>
      </PageLayout>
    )
  }

  const tier = profile.subscriptionTier
  const info = profile.creditInfo
  /* Credit metering: the Assistant and room price tables show only for an enrolled org (`on`). */
  const isEnrolled = isMeteringEnrolled(info)
  // Every dollar figure uses the client's own credit value, set in the backoffice.
  const creditRateCents = creditRateCentsFromInfo(info)

  const isNoPlan = tier === 'locked_free'
  const isTrial = isTrialLike(tier)
  const showSalesButton = isNoPlan || isTrial
  const heading = isNoPlan
    ? t('billing.status.noPlan')
    : isTrial
      ? t('billing.status.trial')
      : t('billing.tiers.enterprise')

  const enabledFeatures = enabledCustomerFeatures(profile.featureFlags)
  const hasFeature = (flag: CustomerFeatureFlag) =>
    enabledFeatures.some((feature) => feature.flag === flag)
  // Contract terms only describe a client on the plan, so a trial or no plan does not claim them.
  const isOnPlan = !isNoPlan && !isTrial
  const paygText = !info?.allowOverage
    ? t('billing.plans.paygOff')
    : info.overageLimitCents === null || info.overageLimitCents === undefined
      ? t('billing.plans.paygNoCap')
      : t('billing.plans.paygCapped', { amount: formatUsd(info.overageLimitCents / 100) })

  const allRows: Array<PlanRow> = [
    {
      id: 'credits',
      label: t('billing.plans.credits'),
      value: isFairgenInternalTier(tier)
        ? t('billing.unlimited')
        : t('billing.plans.creditsPerMonth', {
            count: info?.creditLimit ?? 0,
            formatted: (info?.creditLimit ?? 0).toLocaleString(locale),
          }),
      isShown: true,
    },
    {
      id: 'credit-value',
      label: t('billing.plans.table.creditValue'),
      value: creditRateCents !== null ? formatUsd(creditRateCents / 100) : '',
      isShown: creditRateCents !== null,
    },
    { id: 'pay-as-you-go', label: t('billing.plans.table.payg'), value: paygText, isShown: true },
    {
      // The real per-tier seat cap is not on the profile. Only the unlimited case is known for
      // sure (enterprise), so trial and locked tiers show no seats row.
      id: 'seats',
      label: t('billing.plans.table.seats'),
      value: t('billing.unlimited'),
      isShown: approxMaxSeats(tier) >= UNLIMITED_SEATS_THRESHOLD,
    },
    {
      id: 'professional-services',
      label: t('billing.plans.table.professionalServices'),
      value: t('billing.plans.table.valueDedicatedCsm'),
      isShown: isOnPlan,
    },
    { id: 'studies', label: t('billing.plans.table.studies'), value: true, isShown: hasFeature('createStudy') },
    { id: 'quick-studies', label: t('billing.plans.table.quickStudies'), value: true, isShown: hasFeature('createStudy') },
    { id: 'chat', label: t('billing.plans.rates.chat'), value: true, isShown: hasFeature('chatRoomsEnabled') },
    { id: 'focus-group', label: t('billing.plans.rates.focusGroup'), value: true, isShown: hasFeature('chatRoomsEnabled') },
    {
      id: 'audience-creation',
      label: isOnPlan
        ? t('billing.plans.table.audienceCreationPrivate')
        : t('billing.features.createAudience'),
      value: true,
      isShown: hasFeature('createAudience'),
    },
    { id: 'marketplace', label: t('billing.features.marketplaceAccess'), value: true, isShown: hasFeature('marketplaceAccess') },
    { id: 'assistant', label: t('billing.features.assistantPlatform'), value: true, isShown: hasFeature('assistantPlatform') },
    { id: 'social-listening', label: t('billing.features.socialListening'), value: true, isShown: hasFeature('socialListening') },
    { id: 'web-search', label: t('billing.features.conversationWebSearch'), value: true, isShown: hasFeature('conversationWebSearch') },
    { id: 'connected-sources', label: t('billing.features.agentHandler'), value: true, isShown: hasFeature('agentHandler') },
    { id: 'slack', label: t('billing.features.slackTwins'), value: true, isShown: hasFeature('slackTwins') },
    { id: 'branded-theme', label: t('billing.features.brandedTheme'), value: true, isShown: hasFeature('brandedTheme') },
  ]
  const rows = allRows.filter((row) => row.isShown)

  return (
    <PageLayout className="max-w-full pt-14 py-8 md:pt-8">
      <PageHeader
        className="mt-12 md:mt-0 md:items-end"
        leadingInset={false}
        leading={
          <button
            type="button"
            onClick={() => router.push(ROUTES.settingsTab('billing'))}
            data-testid="pricing-back-link"
            className="relative z-10 flex items-center gap-1.5 text-[14px] text-muted-foreground hover:text-foreground transition-colors w-fit"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('billing.plans.back')}
          </button>
        }
        title={
          <h1
            data-testid="pricing-plan-heading"
            className="font-display text-[2rem] md:text-[36px] leading-[1.2] tracking-tight"
          >
            {heading}
          </h1>
        }
        action={
          showSalesButton ? (
            <Button asChild variant="default" data-testid="pricing-talk-to-sales-button">
              <a href={CONTACT_SALES_URL} target="_blank" rel="noreferrer">
                {t('billing.myPlan.talkToSales')}
              </a>
            </Button>
          ) : null
        }
      />

      <PageContent className="gap-8">
        <TooltipProvider>
          <div
            data-testid="pricing-plan-table"
            className="overflow-hidden rounded-xl border border-border"
          >
            <div className="grid grid-cols-2 bg-muted/50 px-5 py-3">
              <div />
              <Text size="sm" weight="medium" className="text-center">
                {heading}
              </Text>
            </div>
            {rows.map((row, i) => (
              <div
                key={row.id}
                data-testid={`pricing-row-${row.id}`}
                className={cn(
                  'grid grid-cols-2 items-center px-5 py-3',
                  i % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Text size="sm">{row.label}</Text>
                  {row.tooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('billing.plans.table.aboutFeature', { label: row.label })}
                          data-testid={`pricing-row-tooltip-${row.id}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Info className="h-3 w-3" aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs">
                        {row.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
                <PlanRowValue value={row.value} />
              </div>
            ))}
          </div>
        </TooltipProvider>

        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => setIsCostsOpen(true)}
            data-testid="pricing-see-costs"
          >
            {t('billing.plans.seeCosts')}
          </Button>
        </div>
        <HowCreditsWorkDialog open={isCostsOpen} onOpenChange={setIsCostsOpen} enrolled={isEnrolled} />

        <p className="text-center text-sm text-muted-foreground">
          {t('billing.myPlan.talkToSalesPrompt')}{' '}
          <a
            href={CONTACT_SALES_URL}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
            data-testid="pricing-talk-to-sales"
          >
            {t('billing.myPlan.talkToSales')}
          </a>
        </p>
      </PageContent>
    </PageLayout>
  )
}
